import { Injectable, NgZone } from "@angular/core";
import { Observable } from "rxjs";
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import * as THREE from 'three';

export interface Dimensions {
  containerWidth: number,
  containerHeight: number
}

@Injectable({
  providedIn: 'root'
})

export class ShapeService {
  mesh!: THREE.Mesh;
  scene!: THREE.Scene;
  camera!: THREE.PerspectiveCamera;
  renderer!: THREE.WebGLRenderer;
  // controls!: OrbitControls;
  defaultMaterial = new THREE.MeshBasicMaterial({ color: 0x323232 });
  linesMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.5 });
   private httpWithoutInterceptor: HttpClient;

  extrudeSettings = {
    steps: 1,
    depth: 1,
    bevelEnabled: false,
    bevelThickness: 0,
    bevelSize: 0,
    bevelOffset: 0,
    bevelSegments: 0
  };

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private backend: HttpBackend
  ) {
    this.scene = new THREE.Scene();
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setSize(1, 1);
    this.renderer.setClearColor(0xffffff);
     this.httpWithoutInterceptor = new HttpClient(backend);
  }

  /**
   * Retrieves a the remote JS file containing the model assets.
   * 
   * @param url this should be unique for each article
   * @returns observable of remote js file
   * 
   */
  public getRemoteJs(url: string): Observable<any> {
    return this.httpWithoutInterceptor.get(url, {
      responseType: 'text' as 'json'
    });
  }

  /**
   * Creates a mesh group from the passed in shapes array.
   * @param articleShapes: obj.array of shapes from the remote js file
   * @returns a Three.Group[] of meshes 
   */
  public getMeshes(articleShapes: any) {
    const meshes: THREE.Mesh[] = [];
    const meshGroup = new THREE.Group();

    articleShapes.forEach((shape: any) => {
      const extrudeGeometry = new THREE.ExtrudeGeometry(shape, this.extrudeSettings);
      const material = this.getMaterial(shape);
      const mesh = new THREE.Mesh(extrudeGeometry, material)
      meshes.push(mesh);
    });

    meshes.forEach((mesh: any) => {
      const linesMesh = new THREE.LineSegments(mesh.geometry, this.linesMaterial);
      meshGroup.add(mesh);
      meshGroup.add(linesMesh);
    });

    return meshGroup;
  }

  /**
   * Retrieves the article threejs from the remote js file.
   * @param data the remote js file
   * @returns article threejs as string
   */
  public getJsUploadPresignedUrl(articleId: string): Observable<string> {
    return this.http.get(`${environment.apiUrl}/api/DXFReader/Get3JSPresignedURL/${articleId}`, { responseType: 'text' });
  }

  /**
   * Creates and initializes the three.js scene with camera.s
   */
  public initScene(canvasContainer: HTMLElement, dimensions: Dimensions) {
    if (canvasContainer) {
      this.initCamera(dimensions.containerWidth / dimensions.containerHeight);
      this.initLighting();

      this.renderer.setSize(dimensions.containerWidth, dimensions.containerHeight);
      this.renderer.domElement.style.display = 'block';
      canvasContainer.appendChild(this.renderer.domElement);

      this.initControls();
      this.setHelperLinesValue(false);
    } else {
      console.error('Modal container not found.');
    }
  }

  /**
   * Initializes the camera w/ dynamic aspect.
   * @param aspect aspect ratio of the canvas container, calculated in the function body of `initScene()`
   */
  initCamera(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    this.camera.position.z = 75;
    this.camera.position.x = -40;
    this.camera.position.y = 0;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * Adds lighting to the scene.
   */
  private initLighting() {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight.position.set(1, 1, 0);
    this.scene.add(directionalLight);
  }

  /**
   * Initializes the controls for the scene.
   */
  private initControls() {
    // this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.positionControls();
  }

  /**
   * Required for adding the models as meshes to the rendered scene.
   * @param object a mesh or group of meshes
   */
  public addToScene(object: THREE.Object3D) {
    this.scene.add(object);
    this.positionControls();
  }

  /**
   * Positions and updates controls based on scene bounds.
   * Ran on init() to position controls and on addToScene() to update position based on bounds changes from new meshes.
   */
  private positionControls() {
    const center = new THREE.Vector3();
    const boundingBox = new THREE.Box3().setFromObject(this.scene);
    boundingBox.getCenter(center);
    // this.controls.target.copy(center);
    // this.controls.update();
  }

  /**
   * Renders the scene and its objects (extrusions of shapes from remote).
   */
  public renderScene() {
    this.ngZone.runOutsideAngular(() => {
      const animate = () => {
        requestAnimationFrame(animate);
        // Add your animation logic here if needed
        this.renderer.render(this.scene, this.camera);
        // this.controls.update();
      };

      animate();
    });
  }

  /**
   * Updates the dimensions of the scene and triggers camera and controls updates.
   * @param dimensions the dimensions to update the scene with
   */
  public updateDimensions(dimensions: Dimensions) {
    this.renderer.setSize(dimensions.containerWidth, dimensions.containerHeight);
    this.camera.aspect = dimensions.containerWidth / dimensions.containerHeight;
    this.camera.updateProjectionMatrix();
    // this.controls.update();
  }

  /**
   * Animates the scene / main animation loop.
   * Calls itself recursively.
   * Uses requestAnimationFrame() to call itself and call renderScene() from ShapeService.
   * ~~This might not actually be used, I can't tell.~~
   */
  animate() {
    this.renderScene();
    requestAnimationFrame(() => this.animate());
  }

  /**
   * Returns a material for the passed in shape based on `useMaterial` property.
   * Otherwise returns a default material.
   */
  private getMaterial(shape: any) {

    // * Later if we want to map materials, we can update this here.
    // * Each shape contains a useMaterial property that can be used to map materials,
    // * But this codebase lacks the necessary materials to map to.
    // if (shape.useMaterial) {
    //   return shape.useMaterial;
    // } else {
    //   return this.defaultMaterial
    // }

    return this.defaultMaterial;
  }

  /**
   * Removes all meshes from the scene.
   */
  public clearScene() {
    this.scene.clear();
    // this.controls.reset();
  }

  /**
   * Toggles the helper lines on and off.
   * @param value boolean value of the toggle
   */
  public setHelperLinesValue(value: boolean) {
    this.linesMaterial.visible = value;
  }

}